import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Factory, BarChart3, Edit, Trash2, Eye, Play, Pause, Square, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import ManufacturingService from '@/api/manufacturing.service';
import { canCreate, canEdit, canDelete, canView } from '@/lib/permissions';

interface ProductionJob {
  id: number;
  publicId: string;
  businessId: number;
  jobNumber: string;
  recipeId: number;
  recipeName: string;
  plannedQuantity: number;
  producedQuantity: number;
  unit: string;
  status: 'planned' | 'in_progress' | 'paused' | 'completed' | 'cancelled';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  plannedStartDate: string;
  plannedEndDate: string;
  actualStartDate?: string;
  actualEndDate?: string;
  assignedTo?: string;
  notes?: string;
  dateCreated: string;
  dateUpdated: string;
}

const ProductionJobs: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [jobs, setJobs] = useState<ProductionJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  useEffect(() => {
    fetchProductionJobs();
  }, [businessId, currentPage]);

  const fetchProductionJobs = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await ManufacturingService.getAllProductionJobs(businessId, skip, itemsPerPage);
      
      if (response.data.result === 'success') {
        setJobs(response.data?.jobs || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch production jobs",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching production jobs:', error);
      toast({
        title: "Error",
        description: "Failed to fetch production jobs",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (job: ProductionJob, action: 'start' | 'pause' | 'resume' | 'complete' | 'cancel') => {
    if (!canEdit('Manufacturing')) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to modify production jobs",
        variant: "destructive",
      });
      return;
    }

    try {
      switch (action) {
        case 'start':
          await ManufacturingService.startProductionJob(job.publicId);
          break;
        case 'pause':
          await ManufacturingService.pauseProductionJob(job.publicId);
          break;
        case 'resume':
          await ManufacturingService.resumeProductionJob(job.publicId);
          break;
        case 'complete':
          await ManufacturingService.completeProductionJob(job.publicId);
          break;
        case 'cancel':
          await ManufacturingService.cancelProductionJob(job.publicId);
          break;
      }
      
      toast({
        title: "Success",
        description: `Production job ${action}ed successfully`,
      });
      fetchProductionJobs();
    } catch (error) {
      console.error(`Error ${action}ing production job:`, error);
      toast({
        title: "Error",
        description: `Failed to ${action} production job`,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (job: ProductionJob) => {
    if (!canDelete('Manufacturing')) {
      toast({
        title: "Permission Denied",
        description: "You don't have permission to delete production jobs",
        variant: "destructive",
      });
      return;
    }

    if (window.confirm(`Are you sure you want to delete production job "${job.jobNumber}"?`)) {
      try {
        await ManufacturingService.deleteProductionJob(job.publicId);
        toast({
          title: "Success",
          description: "Production job deleted successfully",
        });
        fetchProductionJobs();
      } catch (error) {
        console.error('Error deleting production job:', error);
        toast({
          title: "Error",
          description: "Failed to delete production job",
          variant: "destructive",
        });
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      planned: { variant: 'outline' as const, label: 'Planned' },
      in_progress: { variant: 'default' as const, label: 'In Progress' },
      paused: { variant: 'secondary' as const, label: 'Paused' },
      completed: { variant: 'default' as const, label: 'Completed' },
      cancelled: { variant: 'destructive' as const, label: 'Cancelled' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.planned;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { variant: 'outline' as const, label: 'Low' },
      normal: { variant: 'secondary' as const, label: 'Normal' },
      high: { variant: 'default' as const, label: 'High' },
      urgent: { variant: 'destructive' as const, label: 'Urgent' },
    };
    
    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.normal;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getProgressPercentage = (job: ProductionJob) => {
    return job.plannedQuantity > 0 ? Math.round((job.producedQuantity / job.plannedQuantity) * 100) : 0;
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.jobNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.recipeName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Production Jobs</h1>
          <p className="text-muted-foreground">
            Manage and track your manufacturing production jobs
          </p>
        </div>
        {canCreate('Manufacturing') && (
          <Button onClick={() => navigate('/manufacturing/production-jobs/create')} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Create Job
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
            <Factory className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredJobs.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Play className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredJobs.filter(job => job.status === 'in_progress').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredJobs.filter(job => job.status === 'completed').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Progress</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredJobs.length > 0 ? Math.round(
                filteredJobs.reduce((sum, job) => sum + getProgressPercentage(job), 0) / filteredJobs.length
              ) : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search jobs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md"
            >
              <option value="">All Statuses</option>
              <option value="planned">Planned</option>
              <option value="in_progress">In Progress</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Production Jobs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Production Jobs ({filteredJobs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Job Number</TableHead>
                    <TableHead>Recipe</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Planned Dates</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredJobs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        <div className="flex flex-col items-center gap-2">
                          <Factory className="h-8 w-8 text-muted-foreground" />
                          <p className="text-muted-foreground">No production jobs found</p>
                          {canCreate('Manufacturing') && (
                            <Button onClick={() => navigate('/manufacturing/production-jobs/create')} size="sm">
                              Create your first production job
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredJobs.map((job) => (
                      <TableRow key={job.id}>
                        <TableCell>
                          <div className="font-medium">{job.jobNumber}</div>
                        </TableCell>
                        <TableCell>
                          {job.recipeName}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="text-sm">
                              {job.producedQuantity}/{job.plannedQuantity} {job.unit}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              ({getProgressPercentage(job)}%)
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(job.status)}
                        </TableCell>
                        <TableCell>
                          {getPriorityBadge(job.priority)}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>{new Date(job.plannedStartDate).toLocaleDateString()}</div>
                            <div className="text-muted-foreground">
                              to {new Date(job.plannedEndDate).toLocaleDateString()}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {job.assignedTo || 'Unassigned'}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0">
                                <span className="sr-only">Open menu</span>
                                <div className="h-4 w-4">⋮</div>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              {canView('Manufacturing') && (
                                <DropdownMenuItem onClick={() => navigate(`/manufacturing/production-jobs/${job.publicId}`)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Details
                                </DropdownMenuItem>
                              )}
                              {canEdit('Manufacturing') && (
                                <>
                                  <DropdownMenuItem onClick={() => navigate(`/manufacturing/production-jobs/${job.publicId}/edit`)}>
                                    <Edit className="mr-2 h-4 w-4" />
                                    Edit Job
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  {job.status === 'planned' && (
                                    <DropdownMenuItem onClick={() => handleStatusChange(job, 'start')}>
                                      <Play className="mr-2 h-4 w-4" />
                                      Start Job
                                    </DropdownMenuItem>
                                  )}
                                  {job.status === 'in_progress' && (
                                    <>
                                      <DropdownMenuItem onClick={() => handleStatusChange(job, 'pause')}>
                                        <Pause className="mr-2 h-4 w-4" />
                                        Pause Job
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleStatusChange(job, 'complete')}>
                                        <CheckCircle className="mr-2 h-4 w-4" />
                                        Complete Job
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                  {job.status === 'paused' && (
                                    <DropdownMenuItem onClick={() => handleStatusChange(job, 'resume')}>
                                      <Play className="mr-2 h-4 w-4" />
                                      Resume Job
                                    </DropdownMenuItem>
                                  )}
                                  {(job.status === 'planned' || job.status === 'paused') && (
                                    <DropdownMenuItem onClick={() => handleStatusChange(job, 'cancel')}>
                                      <Square className="mr-2 h-4 w-4" />
                                      Cancel Job
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}
                              <DropdownMenuSeparator />
                              {canDelete('Manufacturing') && job.status !== 'in_progress' && (
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(job)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductionJobs; 